import { useEffect, type ReactElement } from 'react';
import { createPortal } from 'react-dom';

/**
 * **Erst ansehen, dann einfügen** (Sascha, 16. Oktober: „Medien einfügen:
 * man muss es vorher anschauen können“).
 *
 * Über den ganzen Schirm, mit den Bedienelementen des Browsers: Video und Ton
 * laufen gleich los, ein Bild steht da. Gilt für Medien, die schon im
 * Projekt liegen (dann mit „Einfügen“ unten), und für Dateien vom Gerät,
 * bevor sie hochgeladen werden.
 *
 * An `document.body` gehängt: das Blatt darunter ist verschoben, und ein
 * `position: fixed` darin säße in ihm statt über dem Schirm.
 */
export function MedienVorschau({
  name,
  art,
  src,
  poster,
  onClose,
  aktion,
}: {
  name: string;
  art: 'video' | 'audio' | 'image';
  src: string;
  poster?: string | null;
  onClose: () => void;
  aktion?: { text: string; onClick: () => void; aus?: boolean };
}): ReactElement {
  useEffect(() => {
    const taste = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', taste);
    return () => window.removeEventListener('keydown', taste);
  }, [onClose]);

  return createPortal(
    <div className="medien-vorschau" role="dialog" aria-label={`${name} ansehen`} data-medien-vorschau={art}>
      <div className="medien-vorschau-kopf">
        <span className="medien-vorschau-name">{name}</span>
        <button className="small" onClick={onClose} data-vorschau-schliessen>
          Schließen
        </button>
      </div>
      <div className="medien-vorschau-buehne">
        {art === 'image' ? (
          <img src={src} alt={name} />
        ) : art === 'audio' ? (
          <audio src={src} controls autoPlay />
        ) : (
          <video src={src} poster={poster ?? undefined} controls autoPlay playsInline />
        )}
      </div>
      {aktion ? (
        <div className="medien-vorschau-fuss">
          <button
            className="primary"
            style={{ width: '100%' }}
            disabled={aktion.aus}
            onClick={() => {
              aktion.onClick();
              onClose();
            }}
            data-vorschau-aktion
          >
            {aktion.text}
          </button>
        </div>
      ) : null}
    </div>,
    document.body,
  );
}
